/**
 * TypeScript interfaces for admin API response shapes.
 *
 * These mirror the server-side types defined in
 * apps/server/src/routes/admin/orders.ts and dashboard.ts.
 */
import type { FailureDetails, LastRegenFailure } from '@woolaby/types';

// ---------------------------------------------------------------------------
// Order status enum (mirrors apps/server/src/db/schema.ts orderStatusEnum)
// ---------------------------------------------------------------------------

export const ORDER_STATUSES = [
  'INIT',
  'STARTED',
  'PAYMENT_PENDING',
  'PAYMENT_COMPLETED',
  'PAYMENT_FAILED',
  'GENERATING',
  'GENERATING_PLAN',
  'GENERATING_CHARACTER_SHEET',
  'GENERATING_FINAL',
  'PLAN_REVIEW',
  'CHARACTER_SHEET_REVIEW',
  'GENERATION_COMPLETE',
  'GENERATION_FAILED',
  'PREVIEW_READY',
  'CANCELLED',
  'PRINT_JOB_SUBMITTED',
  'SHIPPED',
] as const;

export type OrderStatus = (typeof ORDER_STATUSES)[number];

// ---------------------------------------------------------------------------
// Admin order list item (returned by GET /api/admin/orders)
// ---------------------------------------------------------------------------

export interface AdminOrder {
  id: string;
  email: string | null;
  phone: string | null;
  status: OrderStatus;
  paymentStatus: string | null;
  amountTotal: number | null;
  paidProductType: string | null;
  client: string;
  workflowRunId: string | null;
  generatedPath: string | null;
  appEnv: string | null;
  aiCostCents: number | null;
  luluPrintCostCents: number | null;
  luluShipCostCents: number | null;
  usedFallbackModel: boolean;
  createdAt: string;
  updatedAt: string;
  bookTitle: string | null;
  plotSummary: string | null;
  pageCount: number | null;
  age: number | null;
}

// ---------------------------------------------------------------------------
// Admin order detail (returned by GET /api/admin/orders/:id)
// ---------------------------------------------------------------------------

export interface AdminOrderDetail extends AdminOrder {
  surveyJson: Record<string, unknown> | null;
  billingAddress: Record<string, unknown> | null;
  shippingAddress: Record<string, unknown> | null;
  stripeSessionId: string | null;
  stripePaymentIntentId: string | null;
  threadId: string | null;
  luluJobId: string | null;
  luluStatus: string | null;
  luluTrackingUrl: string | null;
  failureReason: string | null;
  /**
   * Free-form diagnostic message (LLM-authored or recovery-planner detail).
   * Rendered verbatim in the admin panel — the raw dev-jargon leak is
   * intentional (helps support triage; client never branches on it).
   */
  failureMessage: string | null;
  /**
   * Issue #677 — recovery class derived from the `AgentError` taxonomy
   * (`PERMANENT`, `TRANSIENT`, `SAFETY`, `COUNT_MISMATCH`,
   * `CAPACITY_EXCEEDED`, …). Null for legacy / infra failures.
   */
  failureCategory: string | null;
  /**
   * Issue #677 — structured failure diagnostics (per-page retry audit,
   * global regen counter, last skill name). Rendered as a collapsible
   * JSON block. Null for legacy / infra failures.
   */
  failureDetails: FailureDetails | null;
  sessionId: string | null;
  generationDurationMs: number | null;
  pageTextFontSize: number | null;
  environmentConfigJson: Record<string, unknown> | null;
  coverRegenerationCount: number;
  bookRegenerationCount: number;
  pageRegenerationCount: number;
  /**
   * Phase 5 — Retry attempts derived from LangGraph state
   * (`humanPlanApprovalCount`). Counts the times the user approved a plan
   * that was later rejected / resumed. `0` when no thread or no checkpoint.
   */
  retryAttempts: number;
  /**
   * Failure metadata persisted on the order row when a regeneration run
   * failed and the previously-completed book was restored from backup
   * (issue #500). Null/undefined when there is no pending regen failure.
   */
  lastRegenFailure: LastRegenFailure | null;
}

// ---------------------------------------------------------------------------
// Dashboard stats (returned by GET /api/admin/dashboard/stats)
// ---------------------------------------------------------------------------

export interface DailyOrderCount {
  date: string;
  count: number;
}

export interface DashboardStats {
  finishedOrders: number;
  paidUnfinished: number;
  startedUnpaid: number;
  errored: number;
  totalOrders: number;
  totalRevenue: number;
  ordersByStatus: Record<string, number>;
  ordersOverTime: DailyOrderCount[];
}

// ---------------------------------------------------------------------------
// Admin checkpoint list item (returned by GET /api/admin/checkpoints)
// ---------------------------------------------------------------------------

export interface AdminCheckpointListItem {
  threadId: string;
  orderId: string | null;
  orderStatus: OrderStatus | null;
  latestCheckpointId: string | null;
  latestCheckpointAt: string | null;
  checkpointCount: number;
}

// ---------------------------------------------------------------------------
// Admin checkpoint detail (returned by GET /api/admin/checkpoints/:threadId)
// ---------------------------------------------------------------------------

export interface CheckpointEntry {
  checkpointId: string;
  checkpointNs: string;
  parentCheckpointId: string | null;
  channelKeys: string[];
  metadata: Record<string, unknown>;
}

export interface AdminCheckpointDetail {
  threadId: string;
  orderId: string | null;
  orderStatus: OrderStatus | null;
  checkpoints: CheckpointEntry[];
}

// ---------------------------------------------------------------------------
// Admin example list item (returned by GET /api/admin/examples)
// ---------------------------------------------------------------------------

export interface AdminExample {
  id: string;
  title: string;
  childName: string;
  childAge: number;
  theme: string | null;
  artStyle: string | null;
  pageCount: number;
  bookLanguage: string | null;
  storagePath: string;
  sourceOrderId: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /** Signed URL for cover thumbnail, added by the API */
  coverPreviewUrl?: string;
}

// ---------------------------------------------------------------------------
// Admin token response (returned by POST /api/admin/token)
// ---------------------------------------------------------------------------

export interface AdminTokenResponse {
  token: string;
  expiresAt: string;
}

// ---------------------------------------------------------------------------
// Admin customer (returned by GET /api/admin/customers)
// ---------------------------------------------------------------------------

export interface AdminCustomer {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  billingAddress: Record<string, unknown> | null;
  shippingAddress: Record<string, unknown> | null;
  country: string | null;
  marketingOptIn: boolean;
  marketingOptInAt: string | null;
  tosAcceptedVersion: string | null;
  tosAcceptedAt: string | null;
  privacyPolicyAcceptedVersion: string | null;
  privacyPolicyAcceptedAt: string | null;
  biometricConsentVersion: string | null;
  biometricConsentAt: string | null;
  biometricConsentNoticeHash: string | null;
  orderCount: number;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Admin customer detail (returned by GET /api/admin/customers/:id)
// ---------------------------------------------------------------------------

export interface AdminCustomerOrder {
  id: string;
  customerId: string;
  orderId: string;
  orderDate: string;
  productType: string | null;
  amountPaid: number | null;
  currency: string;
  createdAt: string;
}

export interface AdminCustomerDetail extends AdminCustomer {
  customerOrders: AdminCustomerOrder[];
}

// ---------------------------------------------------------------------------
// Admin consent log (returned by GET /api/admin/customers/:id/consent-logs)
// ---------------------------------------------------------------------------

export interface AdminConsentLog {
  id: string;
  customerId: string | null;
  orderId: string | null;
  consentType: string;
  consentVersion: string;
  consentGiven: boolean;
  ipAddress: string | null;
  userAgent: string | null;
  noticeTextHash: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Admin recovery event (returned by GET /api/admin/recovery-events)
// ---------------------------------------------------------------------------

export const RECOVERY_KINDS = [
  'generating',
  'review_silent_replay',
  'review_reemit',
  'review_no_interrupt_id',
  'review_projection_failed',
  'requires_admin_reconciliation',
  'review_recovery_skipped',
  'review_fail_terminal',
  'review_no_op',
] as const;

export type RecoveryKind = (typeof RECOVERY_KINDS)[number];

export interface AdminRecoveryEvent {
  id: string;
  orderId: string;
  threadId: string;
  recoveryKind: RecoveryKind;
  details: Record<string, unknown>;
  createdAt: string;
}
